import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';
import WizardStepLayout from '../../components/common/WizardStepLayout';
import { useSearchExperience } from '../../../search-experience-v2/api/useSearchExperience';
import { ADMIN_RUNTIME_COMMANDS } from '../../../../app/runtime/adminRuntime';
import { ThemePalette } from '../../../search-settings/tabs/ThemeSettings';
import { useLocalizedMessage } from '../../../../util/useLocalizedMessage';

const ThemeStep = ({
  shopId,
  onReady,
  onRegisterSave,
  client,
  runtime,
}) => {
  const localizeMessage = useLocalizedMessage();
  const {
    config,
    updateTheme,
    loadConfig,
    saveConfig,
    error,
    clearError,
  } = useSearchExperience(client, shopId);

  const initialLoadAttemptedRef = useRef(false);

  const renderableError = (() => {
    if (!error) {
      return null;
    }

    if (typeof error === 'string') {
      return error;
    }

    return error.message || error.error
      || localizeMessage('wizard.searchSetup.theme.loadFailed');
  })();

  useEffect(() => {
    if (onReady) {
      onReady(!!config && !renderableError);
    }
  }, [config, onReady, renderableError]);

  useEffect(() => {
    if (config || initialLoadAttemptedRef.current) {
      return;
    }

    initialLoadAttemptedRef.current = true;
    if (renderableError) {
      clearError();
    }
    loadConfig('preview');
  }, [clearError, config, loadConfig, renderableError]);

  // Wrap saveConfig so we persist the integration_step_completed checkpoint
  // to plugin-owned onboarding state only after a successful theme save.
  // We also invalidate any previous preview build because the interface config
  // has changed — step 5 must trigger a fresh gateway preview build.
  const saveWithCheckpoint = useCallback(async (...args) => {
    const result = await saveConfig(...args);

    // Only write bridge actions when saveConfig actually succeeded.
    // saveConfig returns { success: false, ... } on validation/save failure.
    if (
      result
      && result.success !== false
      && runtime?.supports?.(ADMIN_RUNTIME_COMMANDS.ONBOARDING_INTEGRATION_CONFIGURED)
    ) {
      // Await the product lifecycle signal so the plugin summary's first poll
      // observes the completed step and invalidated preview. Cloud is a no-op.
      await runtime.sendMessageWithResponse(
        ADMIN_RUNTIME_COMMANDS.ONBOARDING_INTEGRATION_CONFIGURED,
      );
    }

    return result;
  }, [runtime, saveConfig]);

  useEffect(() => {
    if (onRegisterSave) {
      onRegisterSave(saveWithCheckpoint);
    }
  }, [onRegisterSave, saveWithCheckpoint]);

  if (renderableError) {
    return (
      <WizardStepLayout
        title={localizeMessage('wizard.searchSetup.theme.title')}
        description={localizeMessage('wizard.searchSetup.theme.errorDescription')}
      >
        <Box p={2} data-testid="theme-step-error">
          <Typography color="error">{renderableError}</Typography>
        </Box>
      </WizardStepLayout>
    );
  }

  if (!config) {
    return (
      <WizardStepLayout
        title={localizeMessage('wizard.searchSetup.theme.title')}
        description={localizeMessage('wizard.searchSetup.theme.loading')}
      >
        <Box p={2} data-testid="theme-step-loading">
          <Typography color="textSecondary">
            {localizeMessage('wizard.searchSetup.theme.loading')}
          </Typography>
        </Box>
      </WizardStepLayout>
    );
  }

  return (
    <WizardStepLayout
      title={localizeMessage('wizard.searchSetup.theme.title')}
      description={localizeMessage('wizard.searchSetup.theme.description')}
    >
      <ThemePalette
        theme={config.search?.theme || {}}
        onChange={(key, value) => updateTheme({ [key]: value })}
      />
    </WizardStepLayout>
  );
};

ThemeStep.propTypes = {
  shopId: PropTypes.string.isRequired,
  data: PropTypes.shape({
    selectedExperience: PropTypes.string,
  }),
  onReady: PropTypes.func,
  onRegisterSave: PropTypes.func,
  client: PropTypes.shape({
    loadSearchExperienceConfig: PropTypes.func,
    saveSearchExperienceConfig: PropTypes.func,
  }).isRequired,
  runtime: PropTypes.shape({
    supports: PropTypes.func.isRequired,
    sendMessageWithResponse: PropTypes.func.isRequired,
  }),
};

ThemeStep.defaultProps = {
  data: {},
  onReady: null,
  onRegisterSave: null,
  runtime: null,
};

export default ThemeStep;
